// src/config/navigationConfig.js

import {
  Home, Users, FileText, RefreshCw, ClipboardEdit, Shield, BarChart3,
  UserCircle, Settings, Calendar, MessageSquare, CreditCard, Video,
  Dumbbell, BookOpen, Activity, FolderOpen, ClipboardList, FileSpreadsheet,
  UserPlus, FileSearch, Stethoscope, FlaskConical, Plus, Clock, AlertCircle, Bell,
  HelpCircle, LifeBuoy, ClipboardCheck, Printer, Share2, Mail,
  UserCheck, FileKey, GraduationCap, Scale,
  KeyRound, Briefcase, ToggleLeft, ArrowUpRight, ListPlus, Send, CalendarOff, Star,
  HeartHandshake
} from 'lucide-react';
import { getOrgSpecialties } from '@prism-ehr/shared/utils/specialtyUtils';

// Icon mapping for navigation items
export const navigationIcons = {
  home: Home,
  patients: Users,
  prescriptions: FileText,
  renewalRequests: RefreshCw,
  changeRequests: ClipboardEdit,
  auditLogs: Shield,
  csReports: BarChart3,
  profile: UserCircle,
  admin: Settings,
  users: UserPlus,
  schedule: Calendar,
  messages: MessageSquare,
  billing: CreditCard,
  notifications: Bell,
  telehealth: Video,
  exercises: Dumbbell,
  exerciseLibrary: BookOpen,
  cpgLibrary: Activity,
  rehabPrograms: ClipboardList,
  workoutBuilder: Dumbbell,
  workoutPlans: FolderOpen,
  myTemplates: FileSpreadsheet,
  forms: FileText,
  responses: MessageSquare,
  reports: BarChart3,
  sendHistory: Send,
  encounters: Stethoscope,
  clinicalNotes: FileSearch,
  vitals: Activity,
  soap: ClipboardEdit,
  newPrescription: FileText,
  prescriptionHistory: ClipboardList,
  controlledSubstanceReport: Shield,
  labOrders: FlaskConical,
  orders: ClipboardCheck,
  noteTemplates: ClipboardEdit,
  fax: Printer,
  referralSources: Share2,
  invitedPatients: Mail,
  help: HelpCircle,
  supervisionQueue: UserCheck,
  chartReview: ClipboardCheck,
  collaborativeAgreements: FileKey,
  npTransition: GraduationCap,
  stateRules: Scale,
  roles: KeyRound,
  services: Briefcase,
  features: ToggleLeft,
  referrals: ArrowUpRight,
  plansOfCare: ClipboardCheck,
  eligibilityChecks: FileSearch,
  clinicians: Stethoscope,
  homeHealth: HeartHandshake,
};

// Collapsed icon mapping (emoji fallbacks for unique identification)
export const collapsedIcons = {
  patients: '👥',
  prescribing: '💊',
  exercises: '💪',
  schedule: '📅',
  messages: '💬',
  admin: '⚙️',
  profile: '👤',
  forms: '📋',
  billing: '💰',
  telehealth: '📹',
  clinical: '🏥',
  fax: '📠',
  supervisionQueue: '✅',
  chartReview: '📋'
};

// Get navigation items based on enabled solutions and user role
export function getNavigationItems(solutions, mode, user, counts = {}, permissionChecker = null, hiddenItems = []) {
  const items = [];

  // Permission helper — mirrors getUserMenuItems pattern
  const isAdmin = permissionChecker?.isAdmin ?? (user?.role === 'Admin' || user?.role === 'admin');
  const hasPerm = (perm) => isAdmin || (permissionChecker?.can?.(perm) ?? false);

  // Check if this is exercise-only scenario
  const isExerciseOnlyMultiFeature = mode === 'multi_feature' &&
    (solutions.rehab_prescribed || solutions.ready2exercise) &&
    !solutions.erx_mobile &&
    !solutions.form_builder &&
    !solutions.core_ehr;

  const isExerciseOnlyMode = mode === 'rehab_prescribed' ||
    mode === 'ready2exercise' ||
    isExerciseOnlyMultiFeature;

  // For exercise-only modes, don't show Home - Exercise Library is home
  if (!isExerciseOnlyMode) {
    items.push({
      name: 'Home',
      href: '/',
      icon: navigationIcons.home,
      exact: true
    });
  }

  // eRx Mobile only mode - maintain original structure
  if (mode === 'erx_mobile') {
    items.push(
      { name: 'Patients', href: '/patients', icon: navigationIcons.patients },
      { name: 'Prescriptions', href: '/prescriptions', icon: navigationIcons.prescriptions },
      { 
        name: 'Renewal Requests', 
        href: '/renewal-requests', 
        icon: navigationIcons.renewalRequests,
        badge: counts.pendingRenewalCount 
      },
      {
        name: 'Change Requests',
        href: '/change-requests',
        icon: navigationIcons.changeRequests,
        badge: counts.pendingChangeCount
      }
    );
    return items.filter(item => !hiddenItems.includes(item.name));
  }

  // Exercise-only scenarios (standalone or multi-feature with only exercise)
  if (isExerciseOnlyMode) {
    // Exercise Library first, acting as home
    items.push({ 
      name: 'Exercise Library', 
      href: '/', 
      icon: navigationIcons.exerciseLibrary,
      exact: true 
    });
    
    // Add specific exercise features
    if (solutions.rehab_prescribed || mode === 'rehab_prescribed') {
      items.push(
        { name: 'CPG Library', href: '/cpg-library', icon: navigationIcons.cpgLibrary },
        { name: 'Rehab Programs', href: '/rehab-programs', icon: navigationIcons.rehabPrograms }
      );
    }
    
    if (solutions.ready2exercise || mode === 'ready2exercise') {
      items.push(
        { name: 'Workout Plans', href: '/workout-plans', icon: navigationIcons.workoutPlans }
      );
    }
    
    items.push(
      { name: 'Templates', href: '/templates', icon: navigationIcons.myTemplates }
    );
    
    // Patients last
    items.push({ name: 'Patients', href: '/patients', icon: navigationIcons.patients });

    return items.filter(item => !hiddenItems.includes(item.name));
  }

  // Multi-feature mode with multiple solution types
  if (mode === 'multi_feature') {
    // Patients section - always first when multiple features
    const patientSubNav = [
      { name: 'Patient List', href: '/patients', icon: navigationIcons.patients }
    ];
    
    // Add clinical features if Core EHR is enabled
    if (solutions.core_ehr) {
      patientSubNav.push(
        { name: 'Encounters', href: '/encounters', icon: navigationIcons.encounters },
      );
      if (hasPerm('clinical_manage_templates')) {
        patientSubNav.push(
          { name: 'Note Templates', href: '/note-templates', icon: navigationIcons.noteTemplates }
        );
      }

      if (getOrgSpecialties(user?.organization).includes('physical_therapy') &&
          ['insurance_only', 'hybrid'].includes(user?.organization?.settings?.billing_mode)) {
        patientSubNav.push(
          { name: 'Plans of Care', href: '/plans-of-care', icon: navigationIcons.plansOfCare }
        );
      }

      if (['insurance_only', 'hybrid'].includes(user?.organization?.settings?.billing_mode)) {
        patientSubNav.push(
          { name: 'Eligibility Checks', href: '/eligibility-checks', icon: navigationIcons.eligibilityChecks }
        );
      }
    }

    if (solutions.lab_orders) {
      patientSubNav.push(
        { name: 'Lab Orders', href: '/lab-orders', icon: navigationIcons.labOrders }
      );
    }

    if (solutions.orders) {
      patientSubNav.push(
        { name: 'Orders', href: '/orders', icon: navigationIcons.orders }
      );
    }

    patientSubNav.push(
      { name: 'Invited Patients', href: '/invited-patients', icon: navigationIcons.invitedPatients }
    );

    items.push({
      name: 'Patients',
      href: '/patients',
      icon: navigationIcons.patients,
      collapsedIcon: collapsedIcons.patients,
      subNav: patientSubNav
    });

    // Home Health (solution-gated; hasPerm is true for Admins, so the solution check is required)
    if (solutions.home_health && hasPerm('hh_view')) {
      items.push({
        name: 'Home Health',
        href: '/home-health',
        icon: navigationIcons.homeHealth,
        subNav: [
          { name: 'Dashboard', href: '/home-health', icon: navigationIcons.homeHealth },
          { name: 'Episodes', href: '/home-health/episodes', icon: navigationIcons.patients },
          { name: 'Orders', href: '/home-health/orders', icon: navigationIcons.orders },
        ],
      });
    }

    // Prescribing section if eRx Mobile is enabled
    if (solutions.erx_mobile) {
      items.push({
        name: 'Prescribing',
        href: '/prescriptions',
        icon: navigationIcons.prescriptions,
        collapsedIcon: collapsedIcons.prescribing,
        subNav: [
          { name: 'New Prescription', href: '/patients', icon: navigationIcons.newPrescription },
          { name: 'Prescription History', href: '/prescriptions', icon: navigationIcons.prescriptionHistory },
          { 
            name: 'Renewal Requests', 
            href: '/renewal-requests', 
            icon: navigationIcons.renewalRequests,
            badge: counts.pendingRenewalCount 
          },
          { 
            name: 'Change Requests', 
            href: '/change-requests', 
            icon: navigationIcons.changeRequests,
            badge: counts.pendingChangeCount 
          }
        ]
      });
    }

    // Exercises section if any exercise solution is enabled
    if (solutions.rehab_prescribed || solutions.ready2exercise) {
      const exerciseSubNav = [
        { name: 'Exercise Library', href: '/exercises', icon: navigationIcons.exerciseLibrary }
      ];
      
      if (solutions.rehab_prescribed) {
        exerciseSubNav.push(
          { name: 'CPG Library', href: '/cpg-library', icon: navigationIcons.cpgLibrary },
          { name: 'Rehab Programs', href: '/rehab-programs', icon: navigationIcons.rehabPrograms }
        );
      }
      
      if (solutions.ready2exercise) {
        exerciseSubNav.push(
          { name: 'Workout Plans', href: '/workout-plans', icon: navigationIcons.workoutPlans }
        );
      }
      
      exerciseSubNav.push(
        { name: 'Templates', href: '/templates', icon: navigationIcons.myTemplates }
      );
      
      items.push({
        name: 'Exercises',
        href: '/exercises',
        icon: navigationIcons.exercises,
        collapsedIcon: collapsedIcons.exercises,
        subNav: exerciseSubNav
      });
    }

    // Forms section if Form Builder is enabled
    if (solutions.form_builder) {
      items.push({
        name: 'Forms',
        href: '/forms',
        icon: navigationIcons.forms,
        collapsedIcon: collapsedIcons.forms,
        subNav: [
          { name: 'Form Builder', href: '/forms', icon: navigationIcons.forms },
          { name: 'Responses', href: '/responses', icon: navigationIcons.responses },
          { name: 'Reports', href: '/form-reports', icon: navigationIcons.reports },
          { name: 'Send History', href: '/form-send-history', icon: navigationIcons.sendHistory }
        ]
      });
    }

    // Future features (when implemented)
    if (solutions.features?.scheduling || solutions.scheduling) {
      items.push({
        name: 'Scheduling',
        href: '/appointments/calendar',
        icon: navigationIcons.schedule,
        collapsedIcon: collapsedIcons.schedule,
        subNav: [
          { name: 'Scheduling Requests', href: '/appointments/requests', icon: ClipboardList },
          { name: 'Calendar View', href: '/appointments/calendar', icon: Calendar },
          { name: 'Availability', href: '/availability', icon: Clock },
          { name: 'New Appointment', href: '/appointments/new', icon: Plus },
          ...(user?.organization?.settings?.uses_classes ? [{ name: 'Classes', href: '/classes', icon: Users }] : []),
          // Practice-wide appointment activity (role permission; Admin bypasses)
          ...(hasPerm('scheduling_view_activity') ? [{ name: 'Activity', href: '/appointments/activity', icon: Activity }] : [])
        ]
      });

      // Waitlist — standalone top-level tab (org-gated), role-hideable
      if (user?.organization?.settings?.uses_waitlist) {
        items.push({
          name: 'Waitlist',
          href: '/waitlist',
          icon: ListPlus,
        });
      }
    }

    if (solutions.features?.messaging || solutions.messaging) {
      items.push({
        name: 'Messages',
        href: '/messages',
        icon: navigationIcons.messages,
        collapsedIcon: collapsedIcons.messages,
        badge: counts.unreadMessages,
        subNav: [
          {
            name: 'Inbox',
            href: '/messages',
            icon: navigationIcons.messages,
            badge: counts.unreadMessages
          }
        ]
      });
    }

    if (solutions.features?.fax || solutions.fax) {
      items.push({
        name: 'Fax',
        href: '/fax',
        icon: navigationIcons.fax,
        collapsedIcon: collapsedIcons.fax,
        badge: counts.unreadFaxCount,
        subNav: [
          {
            name: 'Inbox',
            href: '/fax',
            icon: navigationIcons.fax,
            badge: counts.unreadFaxCount
          },
          {
            name: 'New Fax',
            href: '/fax/new',
            icon: Plus
          }
        ]
      });
    }

    // Supervision tools — top-level, permission + relationship gated
    // (also shown to providers with notes assigned to them for signature)
    if (solutions.core_ehr && ((hasPerm('clinical_cosign_notes') && user?.supervisionSummary?.isSupervisor) || user?.supervisionSummary?.hasAssignedNotes)) {
      items.push({
        name: 'Supervision Queue',
        href: '/cosign-queue',
        icon: navigationIcons.supervisionQueue,
        badge: user?.supervisionSummary?.assignedNoteCount || 0,
      });
    }

    if (solutions.core_ehr && hasPerm('clinical_review_charts') && user?.supervisionSummary?.hasNPSupervisees) {
      items.push({
        name: 'Chart Review',
        href: '/chart-review',
        icon: navigationIcons.chartReview,
      });
    }

    // NP self-service items
    if (solutions.core_ehr && user?.supervisionSummary?.isNP) {
      items.push({
        name: 'My Agreements',
        href: '/collaborative-agreements',
        icon: navigationIcons.collaborativeAgreements,
      });
      items.push({
        name: 'My Transition',
        href: '/np-transition',
        icon: navigationIcons.npTransition,
      });
    }
  }

  // Standalone Form Builder
  else if (mode === 'form_builder') {
    items.push(
      { name: 'Forms', href: '/forms', icon: navigationIcons.forms },
      { name: 'Responses', href: '/responses', icon: navigationIcons.responses },
      { name: 'Reports', href: '/form-reports', icon: navigationIcons.reports },
      { name: 'Send History', href: '/form-send-history', icon: navigationIcons.sendHistory },
      { name: 'Patients', href: '/patients', icon: navigationIcons.patients }
    );
  }

  // Standalone Core EHR
  else if (mode === 'core_ehr') {
    items.push(
      { name: 'Patients', href: '/patients', icon: navigationIcons.patients },
      { name: 'Encounters', href: '/encounters', icon: navigationIcons.encounters },
    );
    if (hasPerm('clinical_manage_templates')) {
      items.push(
        { name: 'Note Templates', href: '/note-templates', icon: navigationIcons.noteTemplates }
      );
    }

    if (getOrgSpecialties(user?.organization).includes('physical_therapy') &&
        ['insurance_only', 'hybrid'].includes(user?.organization?.settings?.billing_mode)) {
      items.push(
        { name: 'Plans of Care', href: '/plans-of-care', icon: navigationIcons.plansOfCare }
      );
    }

    if (['insurance_only', 'hybrid'].includes(user?.organization?.settings?.billing_mode)) {
      items.push(
        { name: 'Eligibility Checks', href: '/eligibility-checks', icon: navigationIcons.eligibilityChecks }
      );
    }

    if (solutions.lab_orders) {
      items.push(
        { name: 'Lab Orders', href: '/lab-orders', icon: navigationIcons.labOrders }
      );
    }

    if (solutions.orders) {
      items.push(
        { name: 'Orders', href: '/orders', icon: navigationIcons.orders }
      );
    }

    if (solutions.home_health && hasPerm('hh_view')) {
      items.push({ name: 'Home Health', href: '/home-health', icon: navigationIcons.homeHealth });
    }

    if ((hasPerm('clinical_cosign_notes') && user?.supervisionSummary?.isSupervisor) || user?.supervisionSummary?.hasAssignedNotes) {
      items.push({ name: 'Supervision Queue', href: '/cosign-queue', icon: navigationIcons.supervisionQueue, badge: user?.supervisionSummary?.assignedNoteCount || 0 });
    }

    if (hasPerm('clinical_review_charts') && user?.supervisionSummary?.hasNPSupervisees) {
      items.push({ name: 'Chart Review', href: '/chart-review', icon: navigationIcons.chartReview });
    }

    if (user?.supervisionSummary?.isNP) {
      items.push({ name: 'My Agreements', href: '/collaborative-agreements', icon: navigationIcons.collaborativeAgreements });
      items.push({ name: 'My Transition', href: '/np-transition', icon: navigationIcons.npTransition });
    }
  }

  return items.filter(item => !hiddenItems.includes(item.name));
}

// Helper function to check if we're in exercise-only mode (exported for use in routes)
export function isExerciseOnlyMode(solutions, mode) {
  const isExerciseOnlyMultiFeature = mode === 'multi_feature' && 
    (solutions.rehab_prescribed || solutions.ready2exercise) &&
    !solutions.erx_mobile && 
    !solutions.form_builder && 
    !solutions.core_ehr;
  
  return mode === 'rehab_prescribed' || 
    mode === 'ready2exercise' || 
    isExerciseOnlyMultiFeature;
}

// Get compliance navigation items (only for eRx features)
export function getComplianceNavigation(solutions, hiddenItems = [], permissionChecker = null) {
  if (!solutions.erx_mobile) return [];

  const isAdmin = permissionChecker === null;
  const hasPerm = (perm) => isAdmin || (permissionChecker?.can?.(perm) ?? false);

  const items = [];
  if (hasPerm('admin_view_audit_log')) {
    items.push({ name: 'Audit Logs', href: '/audit-logs', icon: navigationIcons.auditLogs });
  }
  items.push({ name: 'CS Reports', href: '/reports/controlled-substances', icon: navigationIcons.csReports });

  return items.filter(item => !hiddenItems.includes(item.name));
}

// Get user menu items
export function getUserMenuItems(user, isMultiFeature = false, permissionChecker = null, hiddenItems = [], promotedItems = []) {
  const items = [];
  const isAdmin = permissionChecker?.isAdmin ?? (user?.role === 'Admin' || user?.role === 'admin');

  // Permission check helpers — Admin always passes, then fallback to org role permissions
  const hasPerm = (perm) => isAdmin || (permissionChecker?.can?.(perm) ?? false);
  const hasAnyPerm = (perms) => isAdmin || (permissionChecker?.canAny?.(perms) ?? false);

  // All admin-area permissions — if user has ANY of these, show the admin section
  const allAdminPerms = [
    'admin_manage_users',
    'admin_manage_roles',
    'admin_manage_org_settings',
    'billing_manage',
    'billing_view_all',
    'billing_prior_auth_view',
    'referrals_view',
    'referrals_manage',
    'reporting_view',
    'feedback_view_all',
    'feedback_view_own',
    'time_tracking_view',
    'pto_manage',
  ];

  // Clinicians — top-level read-only directory
  if (hasPerm('view_clinicians')) {
    items.push({ name: 'Clinicians', href: '/clinicians', icon: navigationIcons.clinicians });
  }

  // Prior Authorizations — top-level, above Admin (insurance orgs only)
  if (hasPerm('billing_prior_auth_view') && ['insurance_only', 'hybrid'].includes(user?.organization?.settings?.billing_mode)) {
    items.push({ name: 'Prior Auth', href: '/prior-authorizations', icon: navigationIcons.auditLogs });
  }

  // Billing — top-level, above Admin
  if (hasPerm('billing_view_own') || hasPerm('billing_view_all') || hasPerm('billing_manage')) {
    items.push({ name: 'Billing', href: '/billing', icon: navigationIcons.billing });
  }

  // Referral Sources — top-level when promoted, otherwise falls into Admin submenu
  if (hasPerm('referrals_manage') && promotedItems.includes('Referral Sources')) {
    items.push({ name: 'Referral Sources', href: '/referral-sources', icon: navigationIcons.referralSources });
  }

  // Referrals — top-level when promoted, otherwise falls into Admin submenu
  if (hasPerm('referrals_view') && promotedItems.includes('Referrals')) {
    items.push({ name: 'Referrals', href: '/referrals', icon: navigationIcons.referrals });
  }

  // Reporting — top-level when promoted, otherwise falls into Admin submenu
  if (hasPerm('reporting_view') && promotedItems.includes('Reporting')) {
    items.push({ name: 'Reporting', href: '/reporting', icon: navigationIcons.reports });
  }

  // Feedback — top-level when promoted, otherwise falls into Admin submenu (org-gated)
  if ((hasPerm('feedback_view_all') || hasPerm('feedback_view_own')) && promotedItems.includes('Feedback') && user?.organization?.settings?.feedback_enabled === true) {
    items.push({ name: 'Feedback', href: '/feedback', icon: Star });
  }

  // Time Tracking — top-level when promoted, otherwise falls into Admin submenu
  if (hasPerm('time_tracking_view') && promotedItems.includes('Time Tracking') && user?.organization?.settings?.time_tracking?.enabled) {
    items.push({ name: 'Time Tracking', href: '/time-tracking', icon: Clock });
  }

  // PTO Requests — top-level when promoted, otherwise falls into Admin submenu
  if (hasPerm('pto_manage') && promotedItems.includes('PTO Requests') && user?.organization?.settings?.time_tracking?.enabled && user?.organization?.settings?.time_tracking?.pto_enabled) {
    items.push({ name: 'PTO Requests', href: '/pto-requests', icon: CalendarOff });
  }

  // Admin menu - show differently based on mode
  if (isAdmin || hasAnyPerm(allAdminPerms)) {
    if (isMultiFeature) {
      // Build submenu filtered by specific permissions
      const subNav = [];
      if (hasPerm('admin_manage_users'))       subNav.push({ name: 'Users', href: '/users', icon: navigationIcons.users });
      if (hasPerm('admin_manage_roles'))        subNav.push({ name: 'Roles', href: '/roles', icon: navigationIcons.roles });
      if (hasPerm('admin_manage_users'))        subNav.push({ name: 'Supervision', href: '/supervision', icon: navigationIcons.users });
      if (hasPerm('admin_manage_collaborative_agreements') && user?.organization?.np_count > 0) subNav.push({ name: 'Collaborative Agreements', href: '/collaborative-agreements', icon: navigationIcons.collaborativeAgreements });
      if (hasPerm('admin_manage_supervision_rules') && user?.organization?.np_count > 0) subNav.push({ name: 'NP Transition', href: '/np-transition', icon: navigationIcons.npTransition });
      if (hasPerm('admin_manage_org_settings')) subNav.push({ name: 'Services', href: '/services', icon: navigationIcons.services });
      if (hasPerm('admin_manage_org_settings')) subNav.push({ name: 'Organization', href: '/organization', icon: navigationIcons.admin });
      if (hasPerm('admin_manage_org_settings') || hasPerm('notifications_view')) subNav.push({ name: 'Notifications', href: '/notifications', icon: navigationIcons.notifications });
      if (hasPerm('admin_manage_org_settings')) subNav.push({ name: 'Features', href: '/features', icon: navigationIcons.features });
      if (hasPerm('admin_manage_org_settings') && user?.organization?.settings?.uses_veradigm_billing) subNav.push({ name: 'Veradigm PM', href: '/admin/veradigm-pm', icon: navigationIcons.admin });
      if (hasPerm('admin_manage_org_settings') && user?.organization?.settings?.uses_modmed_billing) subNav.push({ name: 'ModMed', href: '/admin/modmed', icon: navigationIcons.admin });
      if (hasPerm('referrals_manage') && !promotedItems.includes('Referral Sources')) subNav.push({ name: 'Referral Sources', href: '/referral-sources', icon: navigationIcons.referralSources });
      if (hasPerm('referrals_view') && !promotedItems.includes('Referrals')) subNav.push({ name: 'Referrals', href: '/referrals', icon: navigationIcons.referrals });
      if (hasPerm('reporting_view') && !promotedItems.includes('Reporting')) subNav.push({ name: 'Reporting', href: '/reporting', icon: navigationIcons.reports });
      if (hasPerm('reporting_view') && user?.organization?.settings?.month_close_enabled === true) subNav.push({ name: 'Month Close', href: '/month-close', icon: navigationIcons.reports });
      if ((hasPerm('feedback_view_all') || hasPerm('feedback_view_own')) && !promotedItems.includes('Feedback') && user?.organization?.settings?.feedback_enabled === true) subNav.push({ name: 'Feedback', href: '/feedback', icon: Star });
      if (hasPerm('time_tracking_view') && !promotedItems.includes('Time Tracking') && user?.organization?.settings?.time_tracking?.enabled) subNav.push({ name: 'Time Tracking', href: '/time-tracking', icon: Clock });
      if (hasPerm('pto_manage') && !promotedItems.includes('PTO Requests') && user?.organization?.settings?.time_tracking?.enabled && user?.organization?.settings?.time_tracking?.pto_enabled) subNav.push({ name: 'PTO Requests', href: '/pto-requests', icon: CalendarOff });

      if (subNav.length > 0) {
        items.push({
          name: 'Admin',
          href: '/admin',
          icon: navigationIcons.admin,
          collapsedIcon: collapsedIcons.admin,
          subNav
        });
      }
    } else {
      // In single-feature mode, show Users if permitted
      if (hasPerm('admin_manage_users')) {
        items.push({
          name: 'Users',
          href: '/users',
          icon: navigationIcons.users
        });
      }
    }
  }

  items.push({
    name: 'Profile',
    href: '/profile',
    icon: navigationIcons.profile,
    collapsedIcon: collapsedIcons.profile
  });

  return items.filter(item => !hiddenItems.includes(item.name));
}

// Help navigation item — always visible in all modes
// subNav includes Help Center (link) and Support (action — handled by Sidebar)
export function getHelpNavItem() {
  return {
    name: 'Help',
    href: '/help',
    icon: navigationIcons.help,
    subNav: [
      { name: 'Help Center', href: '/help', icon: HelpCircle },
      { name: 'Support', href: '#support', icon: LifeBuoy },
    ]
  };
}